import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import cn from 'classnames';

import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { useCases } from '@/features/vfo/hooks/useCases';
import { Form, FormItem } from '@/features/vfo/uikit/Form';
import { ApiUpdateCaseMemberSelfDtoV2, ProfileType, useApi } from '@/shared/api';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { useAuthStore } from '@/shared/store/auth.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SetupProfileModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

import {
  Actions,
  AvatarWrapper,
  ContentWrapper,
  FormItems,
  InfoPreview,
  Introduction,
  IntroductionContent,
  IntroName,
  IntroTextWrapper,
  RoleWrapper,
  Text,
} from './styled';

type FormValues = {
  firstName: string;
  lastName: string;
  country?: ApiUpdateCaseMemberSelfDtoV2['country'];
};

export const SetupProfileModal: FC<SetupProfileModalProps> = ({
  withIntroduction,
  initialData,
  isMember,
  onSave,
}) => {
  const { t: tc } = useTranslation('common');
  const { t: ts } = useTranslation('widgets/modals', { keyPrefix: 'setupProfile' });
  const profileType = useProfileStore((s) => s.profileType);
  const account = useAuthStore((s) => s.account);
  const modals = useModalsStore();
  const api = useApi();
  const values = useI18nValues();
  const { getMemberCases } = useCases();
  const {
    control,
    watch,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      firstName: initialData.firstName,
      lastName: initialData.lastName,
      country: isMember ? initialData.country : undefined,
    },
    mode: 'onChange',
  });

  const firstNameValue = watch('firstName');
  const lastNameValue = watch('lastName');

  const onSubmit = async ({ firstName, lastName, country }: FormValues) => {
    if (!account?.id) return;
    const { data: updatedUser } = await api.updateUser({ firstName, lastName });

    if (isMember) {
      const caseData = getMemberCases(account.id)[0];
      if (!caseData) return;
      await api.casesUpdateSelf({ country });
    }

    // TODO: remove profilesUpdate after moving to auth service
    if (profileType === ProfileType.MAIN_USER) {
      await api.profilesUpdate({ firstName, lastName });
    }

    if (updatedUser) onSave?.({ ...updatedUser, country });
    modals.closeModal();
  };

  return (
    <ModalContainerV2 title={ts('title')} pinBottom closable={!withIntroduction}>
      <Form onFinish={formHandleSubmit(onSubmit)}>
        <ContentWrapper>
          {withIntroduction && (
            <Introduction>
              <InfoPreview>
                <AvatarWrapper className={cn({ isMember })}>
                  {firstNameValue?.[0]}
                  {lastNameValue?.[0]}
                </AvatarWrapper>
                <IntroTextWrapper>
                  <RoleWrapper>
                    {!isMember && <Icon name="medal" size="s12" />}
                    <p>
                      {initialData.tag && isMember
                        ? values.tag(initialData.tag)
                        : ts('introduction.roles.founder')}
                    </p>
                  </RoleWrapper>
                  <IntroName className="truncate">
                    {firstNameValue} {lastNameValue}
                  </IntroName>
                </IntroTextWrapper>
              </InfoPreview>
              <IntroductionContent>
                <Title bold size={18}>
                  {ts('introduction.title')}
                </Title>
                <Text>
                  {ts.rich(isMember ? 'introduction.textMember' : 'introduction.textFounder')}
                </Text>
              </IntroductionContent>
            </Introduction>
          )}
          <FormItems>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: ts('fields.firstName.error') }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={ts('fields.firstName.label')} required>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input
                      placeholder={ts('fields.firstName.placeholder')}
                      type="text"
                      {...field}
                    />
                  </FormItem>
                </FieldContainerIw>
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: ts('fields.lastName.error') }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={ts('fields.lastName.label')} required>
                  <FormItem
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input placeholder={ts('fields.lastName.placeholder')} type="text" {...field} />
                  </FormItem>
                </FieldContainerIw>
              )}
            />
          </FormItems>
          {isMember && (
            <Controller
              name="country"
              control={control}
              rules={{ required: ts('fields.country.error') }}
              render={({ field, fieldState }) => (
                <FieldContainerIw label={ts('fields.country.label')} required>
                  <FormItem validateStatus={fieldState.error ? 'error' : ''}>
                    <SelectCountry placeholder={ts('fields.country.placeholder')} {...field} />
                  </FormItem>
                </FieldContainerIw>
              )}
            />
          )}
        </ContentWrapper>
        <Actions>
          {!withIntroduction && (
            <Button type="default" onClick={modals.closeModal} size="middle" htmlType="button">
              {tc('cancel')}
            </Button>
          )}
          <Button loading={isSubmitting} size="middle" htmlType="submit">
            {tc('save')}
          </Button>
        </Actions>
      </Form>
    </ModalContainerV2>
  );
};
